// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { StyledTypography } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import Notify from "@rugby/App/Components/Notify";
import { StyledPopupHeader } from "@rugby/App/Components/Tippy/Style";
import { signInStyles, xngSignInExistingAccountStyles } from "@rugby/App/Components/XngSignIn/XngSignIn.style";
import { spaceValidation } from "@rugby/XDL/Utilities/utilities";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");

    return {
        isSigningIn: state.context.isSigningIn,
        session: state.context.session,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        accountSettings: getSettingsCollection(state).accountSettings,
        customSettings: getSettingsCollection(state).customSettings as any,
    };
};

const mapDispatchToProps = {
    signIn,
};

const styles = { ...signInStyles, ...xngSignInExistingAccountStyles };

const XngSignIn: FC<Props> = (props: Props) => {
    const [rememberMe, setRememberMe] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [userName, setUserName] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [isSignInClicked, setIsSignInClicked] = React.useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = React.useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const { accountSettings, customSettings } = props;

    const prefix = customSettings?.usernamePrefix;
    const { showRegistrationLink } = customSettings;

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;

        setIsSignInClicked(true);
        if (!userName || !password) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        const queryParams = parseQueryString<{ returnUrl?: string }>(window.location.search);
        const returnUrl = queryParams.returnUrl?.toString();
        setErrorMessage("");

        props.signIn({
            userName: `${prefix || ""}${userName}`,
            password: pass,
            rememberMe,
            returnUrl,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setErrorMessage(error);
            },
        });
    };

    const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            signInHandler();
        }
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const handleCreateNewAccount = () => {
        const { history, createAccountUrl } = props;

        if (!createAccountUrl) {
            return;
        }

        hideTippy();
        history.push(createAccountUrl);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if ((accountSettings.useEmailAsUserName && !validateEmail(userName)) || !spaceValidation(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const userErrorMessage = validateUserName();

    const passwordErrorMessage =
        !password && isSignInClicked
            ? siteMessage("SignInInfo_Password_Required")
            : password && !spaceValidation(password)
            ? siteMessage("SignInInfo_Password_Validation")
            : null;

    return (
        <StyledWrapper {...styles.main}>
            <StyledPopupHeader>
                <Typography as="h4">{translate("Welcome!")}</Typography>
            </StyledPopupHeader>
            <StyledWrapper {...styles.content}>
                <StyledWrapper {...styles.userNameWrapper}>
                    <TextField
                        id="userName"
                        {...styles.userNameTextField}
                        label={translate("User Name")}
                        onChange={e => setUserName(e.currentTarget.value)}
                        value={userName}
                        error={userErrorMessage}
                        autoComplete="username"
                        data-test-selector="signIn_userName"
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.passwordWrapper}>
                    <TextField
                        id="password"
                        label={translate("Password")}
                        onChange={e => setPassword(e.currentTarget.value)}
                        onKeyPress={onKeyPress}
                        value={password}
                        error={passwordErrorMessage}
                        iconProps={{ ...styles.icon, src: showPassword ? EyeOff : Eye }}
                        iconClickableProps={{
                            onClick: () => {
                                setShowPassword(!showPassword);
                            },
                        }}
                        clickableText={password}
                        autoComplete="current-password"
                        data-test-selector="signIn_password"
                        type={showPassword ? "text" : "password"}
                    />
                </StyledWrapper>
                {errorMessage && (
                    <StyledWrapper>
                        <Notify messageType="danger" body={errorMessage} style={{ marginBottom: "20px" }} />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.forgetPasswordWrapper}>
                    <Link onClick={() => setIsResetPasswordModalOpen(true)} data-test-selector="signIn_forgotPassword">
                        {translate("Forgot Password")}
                    </Link>
                    <Modal
                        headline={translate("Reset Password")}
                        {...styles.resetPasswordModal}
                        isOpen={isResetPasswordModalOpen}
                        handleClose={() => onResetPasswordClose()}
                        data-test-selector="resetPasswordModal"
                    >
                        <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                    </Modal>
                </StyledWrapper>
                <StyledWrapper {...styles.sginInWrapper}>
                    <CheckboxGroup>
                        <Checkbox
                            uid="rememberMe"
                            {...styles.rememberMeCheckbox}
                            checked={rememberMe}
                            onChange={(e, value) => setRememberMe(value)}
                        >
                            {translate("Remember Me")}
                        </Checkbox>
                    </CheckboxGroup>

                    <Button {...styles.signInButton} onClick={() => signInHandler()} data-test-selector="signIn_submit">
                        {translate("Sign In")}
                    </Button>
                    <Modal
                        headline={translate("Change Password")}
                        {...styles.changePasswordModal}
                        isOpen={isChangePasswordModalOpen}
                        handleClose={onChangePasswordClose}
                    >
                        <SignInChangePasswordForm
                            enteredUserName={userName}
                            enteredPassword={password}
                            instructionsText={translate(
                                "A password change is required for your account before you may continue.",
                            )}
                            onPasswordChanged={onPasswordChanged}
                        />
                    </Modal>
                </StyledWrapper>

                {showRegistrationLink && (
                    <StyledWrapper {...styles.newToRugbyWrapperStyle}>
                        <StyledTypography {...styles.registrationStyle?.newToRugbyStyle}>
                            {translate("New to Rugby?")}
                        </StyledTypography>
                        <Link {...styles.registrationStyle?.registrationLinkStyle} onClick={handleCreateNewAccount}>
                            {translate("Register Here")}
                        </Link>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(XngSignIn));
